import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from '../../../../hooks';
import proofkitFilemakerService from '../../../../services/proofkitFilemakerService';
import { ArrowLeft, Save } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, Button, Input, Label, Textarea, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../components/ui';
import { ZContacts } from '../../../../schema/Contacts';

const CONTACTS_LAYOUT = process.env.REACT_APP_FILEMAKER_LAYOUT_CONTACTS || "contacts";

const createId = () => {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

export default function ContactsForm() {
  const navigate = useNavigate();
  const { toast } = useToast();

  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    nombre: "",
    apellido: "",
    email: "",
    telefono: "",
    empresa: "",
    cargo: "",
    estado: "activo",
    notas: "",
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData((p) => ({ ...p, [name]: value }));
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const onSubmit = async (e) => {
    e.preventDefault();

    const nombre = String(formData?.nombre || "").trim();
    const apellido = String(formData?.apellido || "").trim();
    const email = String(formData?.email || "").trim();

    if (!nombre) {
      return toast({ title: "Falta nombre", description: "Debes indicar el nombre del contacto", variant: "destructive" });
    }

    if (!email) {
      return toast({ title: "Falta email", description: "Debes indicar el email del contacto", variant: "destructive" });
    }

    if (!validateEmail(email)) {
      return toast({ title: "Email inválido", description: "El formato del email no es válido", variant: "destructive" });
    }

    try {
      setSaving(true);

      const contactFieldData = ZContacts
        .pick({ nombre: true, apellido: true, email: true })
        .partial()
        .parse({ nombre, apellido, email });

      await proofkitFilemakerService.createRecord(CONTACTS_LAYOUT, contactFieldData);

      toast({ title: "Contacto creado", description: "El contacto fue creado exitosamente" });
      navigate("/campanas/contacts");
    } catch (err) {
      console.error("Error creating contact:", err);
      toast({ title: "Error", description: "No se pudo crear el contacto", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <Button variant="ghost" onClick={() => navigate("/campanas/contacts")}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Volver
        </Button>
        <Button onClick={onSubmit} disabled={saving}>
          <Save className="h-4 w-4 mr-2" />
          {saving ? "Guardando..." : "Crear Contacto"}
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Nuevo Contacto</CardTitle>
        </CardHeader>
        <CardContent>
          <form className="space-y-6" onSubmit={onSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="nombre">Nombre *</Label>
                <Input 
                  id="nombre" 
                  name="nombre" 
                  value={formData.nombre} 
                  onChange={handleInputChange} 
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="apellido">Apellido</Label>
                <Input 
                  id="apellido" 
                  name="apellido" 
                  value={formData.apellido} 
                  onChange={handleInputChange} 
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email *</Label>
                <Input 
                  id="email" 
                  name="email" 
                  type="email"
                  value={formData.email} 
                  onChange={handleInputChange} 
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="telefono">Teléfono</Label>
                <Input 
                  id="telefono" 
                  name="telefono" 
                  value={formData.telefono} 
                  onChange={handleInputChange} 
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="empresa">Empresa</Label>
                <Input 
                  id="empresa" 
                  name="empresa" 
                  value={formData.empresa} 
                  onChange={handleInputChange} 
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="cargo">Cargo</Label>
                <Input 
                  id="cargo" 
                  name="cargo" 
                  value={formData.cargo} 
                  onChange={handleInputChange} 
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Estado</Label>
                <Select value={formData.estado} onValueChange={(v) => handleSelectChange("estado", v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecciona estado" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="activo">Activo</SelectItem>
                    <SelectItem value="inactivo">Inactivo</SelectItem>
                    <SelectItem value="deshabilitado">Deshabilitado</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="notas">Notas</Label>
              <Textarea 
                id="notas" 
                name="notas" 
                value={formData.notas} 
                onChange={handleInputChange}
                rows={4}
                placeholder="Información adicional sobre el contacto..."
              />
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
